/**
 * Session state of the preview panel that is not part of the editor store contract (W2-02): the Edit-mode view
 * transform (zoom / pan), the "Set parent..." pick mode, the line under the last right click (context menu
 * commands act on it), the last line click (click again at the same spot cycles through overlapping lines) and the
 * Phira settings popover (open, and the focus grace of a keyboard / palette / menu opening), and the built-in
 * effects drawn in the last frame (the toolbar's "approximate" badge). One store per
 * AppServices; React reads it with subscribe/getSnapshot.
 */

import type { AppServices } from '../../app/registry/types';
import type { LineId } from '../../core/model/types';
import type { Unsubscribe, Vec2 } from '../../core/util/types';
import { appKey } from './live';
import { IDENTITY_VIEW, type StageView } from './stageGeometry';
import { NO_BUILTIN_EFFECTS, type BuiltinEffectsShown } from './effectBadges';

export interface ContextTarget {
  /** Topmost line under the right click (null = empty space). */
  readonly line: LineId | null;
  /** Click position in world (chart space) and RPE coordinates. */
  readonly world: Vec2;
  readonly rpe: Vec2;
  /** Wall time (ms) of the right click. */
  readonly at: number;
}

export interface StageSnapshot {
  readonly view: StageView;
  /** "Set parent...": the line whose new parent the next click picks. */
  readonly parentPick: LineId | null;
  readonly contextTarget: ContextTarget | null;
  readonly phiraOpen: boolean;
  /**
   * When `preview.phiraSettings` ran from a non-pointer source (palette, key, menu...): the time (`stageClock`, ms)
   * from which the popover ignores the opener handing focus back (PhiraSettingsPopover.tsx). null = no grace: the
   * popover was opened with a pointer (its trigger, a button) or a pointer press outside has ended the grace.
   */
  readonly phiraGraceAt: number | null;
  /** Built-in effects drawn in the last frame, by fidelity (the same object until the names change). */
  readonly builtinEffects: BuiltinEffectsShown;
}

/** Monotonic clock of the stage state (ms). */
export const stageClock = (): number => (typeof performance === 'undefined' ? Date.now() : performance.now());

const INITIAL: StageSnapshot = Object.freeze({
  view: IDENTITY_VIEW,
  parentPick: null,
  contextTarget: null,
  phiraOpen: false,
  phiraGraceAt: null,
  builtinEffects: NO_BUILTIN_EFFECTS,
});

export class StageState {
  private snap: StageSnapshot = INITIAL;
  private readonly listeners = new Set<() => void>();
  /** Last left click on lines (cycling): position in CSS px of the stage and the candidates in order. */
  lastClick: { readonly x: number; readonly y: number; readonly candidates: readonly LineId[] } | null = null;

  subscribe(cb: () => void): Unsubscribe {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  getSnapshot = (): StageSnapshot => this.snap;

  set(patch: Partial<StageSnapshot>): void {
    let changed = false;
    for (const k of Object.keys(patch) as (keyof StageSnapshot)[]) {
      if (!Object.is(this.snap[k], patch[k])) changed = true;
    }
    if (!changed) return;
    this.snap = { ...this.snap, ...patch };
    for (const cb of this.listeners) cb();
  }
}

const states = new WeakMap<object, StageState>();

/** The stage state of an app (shared by the services and every command context copy). */
export function stageStateOf(services: AppServices): StageState {
  const key = appKey(services);
  let s = states.get(key);
  if (s === undefined) {
    s = new StageState();
    states.set(key, s);
  }
  return s;
}
